import { cloneDeep, uniqBy } from "lodash"
import moment from "moment"
import React from "react"
import { Linking, Platform, Text } from "react-native"
import DeviceInfo from "react-native-device-info"
import { showMessage } from "react-native-flash-message"
import { useMediaQuery } from "react-responsive"

import Api from "../modules/api"
import Sound from "../modules/audio"
import Alert from "../views/common/Alert"
import Constants from "./Constants"

const callNumber = function (number) {
  let tel = number.replace(/\s/g, "")
  Linking.openURL(`tel://${tel}`).catch(error => {
    Alert.show(
      "Support",
      `\nYour device does not support calling.\n\nPlease dial ${number} from another phone.`,
    )
  })
}

const sendEmail = function (address) {
  Linking.openURL(`mailto://${address}`).catch(error => {
    Alert.show(
      "Support",
      `\nYour device has no email accounts set up.\n\nPlease add one and try again.`,
    )
  })
}

const androidFontFix = () => {
  if (Platform.OS !== "android") {
    return
  }

  const oldRender = Text.render
  Text.render = function (...args) {
    const origin = oldRender.call(this, ...args)
    return React.cloneElement(origin, {
      textBreakStrategy: "simple",
    })
  }
}

function versionNumber() {
  const OS = Platform.OS === "ios" ? "P" : "A"
  const OSVersion = Number(Platform.Version) ?? "0"
  const appVersion = DeviceInfo.getVersion() ?? "0"
  const buildVersion = DeviceInfo.getBuildNumber() ?? "0"
  return `Ver: ${OS}${OSVersion}-${appVersion}-${buildVersion}`
}

export function Desktop({ children }) {
  const isDesktop = useMediaQuery({ minWidth: 992 })
  return isDesktop ? children : null
}
export function Tablet({ children }) {
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 991 })
  return isTablet ? children : null
}
export function Mobile({ children }) {
  const isMobile = useMediaQuery({ maxWidth: 767 })
  return isMobile ? children : null
}

function statusForAction(order, action) {
  if (order.currentStatus === Constants.status.PENDING) {
    return action === "positive"
      ? Constants.status.ACCEPTED
      : Constants.status.REJECTED
  } else if (order.currentStatus === Constants.status.ACCEPTED) {
    return action === "positive"
      ? Constants.status.READY
      : Constants.status.CANCELLED
  } else if (order.currentStatus === Constants.status.READY) {
    return action === "positive"
      ? Constants.status.COMPLETED
      : Constants.status.CANCELLED
  }
  return Constants.status.COMPLETED
}

function showNewOrderMessage(order, onPress) {
  Sound.playAlert()

  const time = order?.type === "OAT" ? order.createdAt : order.timeslot
  const timeString = moment(time).format("HH:mm")
  let message =
    order?.type !== "OAT"
      ? `Collection requested for ${timeString}. Tap for details.`
      : `New order received at ${timeString}. Tap for details.`

  showMessage({
    message: "New Order!",
    description: message,
    type: "info",
    icon: "auto",
    style: { paddingVertical: 40 },
    backgroundColor: Constants.colours.appGreen,
    titleStyle: {
      fontFamily: Constants.fonts.appExtraBold,
      fontSize: 18,
      paddingTop: 3,
    },
    textStyle: {
      fontFamily: Constants.fonts.appSemiBold,
      fontSize: 16,
      marginRight: 15,
    },
    duration: 12000,
    onPress,
  })
}

function showRejectedMessage(order) {
  let time = moment(order.timeslot || order.createdAt).format("HH:mm")
  Sound.playAlert()

  Alert.show(
    "Pending Order Timeout!",
    `\nOrder number #${order.reference} for ${time} by ${order.customer.firstName} was automatically rejected due to timeout.
		\n\nPlease make sure to accept or reject new orders within 10 minutes.`,
  )
}

async function logoutUser(completed = () => {}) {
  return Alert.showTwoButton(
    "Sign Out",
    `\nAre you sure you want to sign out of Orderbee?`,
    {
      text: "Sign Out",
      onPress: async () => {
        let success = await Api.logout()
        console.log(success)
        completed(success)
      },
    },
    { text: "Cancel", onPress: () => completed(false) },
  )
}

function menuItemHasContent(item) {
  return (
    item &&
    ((item.items && item.items.length > 0) ||
      (item.subCategories && item.subCategories.length > 0) ||
      (item.choiceGroups && item.choiceGroups.length > 0) ||
      (item.categories && item.categories.length > 0) ||
      item.price)
  )
}

const getAllCategoryItems = category => {
  const clonedCategory = cloneDeep(category)
  let items = [].concat(clonedCategory?.items ?? [])

  clonedCategory?.subCategories?.forEach(sub => {
    items = items?.concat(getAllCategoryItems(sub))
  })

  return items
}

function getAllMenuItems(menu) {
  const clonedMenu = cloneDeep(menu)
  if (!menuItemHasContent(clonedMenu)) {
    return []
  }

  let items = []
  clonedMenu?.categories?.forEach(category => {
    items = items?.concat(getAllCategoryItems(category))
  })

  return uniqBy(items, "_id")
}

function getAllChoiceOptions(item) {
  if (!item.choiceGroups || item.choiceGroups.length === 0) return []
  let options = []
  item.choiceGroups.forEach(group => {
    options.concat(group.items ?? [])
  })
  return options
}

const populateChoice = (itemId, choiceGroupId) => _choice => {
  const {
    name,
    customerShortDescription,
    customerShortDesc,

    // remove useless props
    customerLongDescription,
    customerLongDesc,
    description,
    instruction,
    defaultSelection,
    enabledForOat,
    enabledForTakeaway,
    product,
    sequenceForOat,
    sequenceForTakeaway,
    // end
    ...choice
  } = cloneDeep(_choice)

  choice.name =
    customerShortDescription ?? customerShortDesc ?? name ?? instruction ?? ""
  choice.groupId = choiceGroupId
  choice.itemId = itemId
  choice.__type = choice.__type || "MenuChoiceGroupItem"
  return choice
}

const populateChoiceGroup = itemId => _choiceGroup => {
  const {
    name,
    customerShortDescription,
    customerShortDesc,

    // remove useless props
    customerLongDescription,
    customerLongDesc,
    description,
    enabledForOat,
    enabledForTakeaway,
    mandatory,
    maxSelection,
    maxSelectionForOat,
    maxSelectionForTakeaway,
    minSelection,
    minSelectionForOat,
    minSelectionForTakeaway,
    code,
    // end
    ...choiceGroup
  } = cloneDeep(_choiceGroup)

  choiceGroup.name = customerShortDescription ?? customerShortDesc ?? name ?? ""
  choiceGroup.itemId = itemId
  choiceGroup.groupId = choiceGroup._id
  choiceGroup.__type = choiceGroup.__type || "MenuChoiceGroup"
  choiceGroup.items = choiceGroup.choices.map(
    populateChoice(itemId, choiceGroup._id),
  )
  delete choiceGroup.choices
  return choiceGroup
}

function populateItem(_item) {
  const {
    name,
    customerShortDescription,
    customerShortDesc,

    // remove useless props
    customerLongDescription,
    customerLongDesc,
    description,
    restrictions,
    // end
    ...item
  } = cloneDeep(_item)

  item.name = customerShortDescription ?? customerShortDesc ?? name ?? ""

  item.choiceGroups = item.choiceGroups.map(populateChoiceGroup(item._id))
  item.itemId = item._id
  item.__type = item.__type || "MenuItem"
  return item
}

function populateCategory(_category, parentCatId) {
  const {
    name,
    customerShortDescription,
    customerShortDesc,

    // remove useless props
    customerLongDescription,
    customerLongDesc,
    description,
    groupCode,
    enabledUntil,
    disabledUntil,
    setCode,
    timePeriods,
    // end
    ...category
  } = cloneDeep(_category)

  category.name = customerShortDescription ?? customerShortDesc ?? name ?? ""
  category.categoryId = category._id
  category.__type = "MenuCategory"
  if (parentCatId) {
    category.parentCatId = parentCatId
  }
  category.subCategories = category.subCategories.map(sub =>
    populateCategory(sub, category._id),
  )
  category.items = (category.items || []).map(populateItem)
  return category
}

function processFlooidMenuCategory(menu, category) {
  if (!menu || !category) {
    return null
  }
  const _category = cloneDeep(category)

  _category.items = Object.values(category.items || []).map(
    itemId => menu.items?.[itemId],
  )
  if (_category.subCategories?.length) {
    _category.subCategories = _category.subCategories.map(sub =>
      processFlooidMenuCategory(menu, sub),
    )
  } else {
    _category.subCategories = []
  }
  return _category
}

function processFlooidMenu(data) {
  if (!data?.categories) {
    return {}
  }
  const menu = { ...data }

  if (menu.provider === "Flooid") {
    Object.values(menu.items || {}).forEach(item => {
      item.choiceGroups = (item.choiceGroups || []).map(
        groupId => menu.choiceGroups[groupId],
      )
    })

    menu.categories = (menu.categories || [])?.map(category =>
      processFlooidMenuCategory(menu, category),
    )
  }

  delete menu.items
  delete menu.choiceGroups

  return menu
}

function populateMenuIds(data) {
  const menu = processFlooidMenu(data)
  if (!menu?.categories) {
    return {}
  }

  try {
    menu.categories = (menu.categories || []).map(category =>
      populateCategory(category),
    )
    return menu
  } catch (error) {
    console.log(error)
    return data
  }
}

// Takes a menu item/category/choiceGroup/choice, finds it in the menu (called data) and toggles the boolean with provided key.
// Returns a new object with these changes
function toggleCategory(_category, id, key, value) {
  const category = cloneDeep(_category)
  if (category._id === id) {
    category[key] = value
  } else {
    category.items.forEach(item => {
      if (item._id === id) {
        item[key] = value
      } else {
        item.choiceGroups.forEach(choiceGroup => {
          if (choiceGroup._id === id) {
            choiceGroup[key] = value
          } else {
            choiceGroup.items.forEach(choice => {
              if (choice._id === id) {
                choice[key] = value
              }
            })
          }
        })
      }
    })
  }

  category.subCategories = cloneDeep(
    category.subCategories ?? [],
  ).map(subCategory => toggleCategory(subCategory, id, key, value))

  return category
}

function toggleItemValue(menu, id, key, value) {
  if (!menu || !id || !key) {
    return
  }
  try {
    const _menu = cloneDeep(menu)
    _menu.categories = (_menu.categories ?? []).map(category =>
      toggleCategory(category, id, key, value),
    )
    return _menu
  } catch (error) {
    console.log(error)
  }
}

const findItemInCategory = (category, id) => {
  for (const c in category?.items ?? []) {
    const _item = category?.items[c]

    if (_item._id === id) {
      return _item
    } else {
      for (const g in _item?.choiceGroups ?? []) {
        const _group = _item?.choiceGroups[g]

        if (_group._id === id) {
          return _group
        } else {
          for (const i in _group?.items ?? []) {
            const _choice = _group?.items[i]

            if (_choice._id === id) {
              return _choice
            }
          }
        }
      }
    }
  }

  let item
  for (const s in category?.subCategories ?? []) {
    const subCat = category?.subCategories[s]
    item = findItemInCategory(subCat, id)

    if (item) {
      break
    }
  }

  return item
}

const getMenuItemById = (menu, id) => {
  for (const c in menu?.categories ?? []) {
    const category = menu?.categories[c]
    if (category._id === id) {
      return category
    }
    const item = findItemInCategory(category, id)

    if (item) {
      return item
    }
  }
}

export default {
  callNumber,
  sendEmail,
  androidFontFix,
  statusForAction,
  showNewOrderMessage,
  showRejectedMessage,
  logoutUser,
  menuItemHasContent,
  getAllMenuItems,
  getAllChoiceOptions,
  populateMenuIds,
  toggleItemValue,
  versionNumber,
  getMenuItemById,
}
